/*
 * Model for mapped content
 */

define([
  "backbone",
  "jquery",
  "configuration/properties",
  "messaging/messenger",
  "modules/shared/sortableList/sortableListModel",
  "modules/shared/history/historyModel",
], function (Backbone, $, Properties, Messenger, SortableListModel, HistoryModel) {
  var ContentModel = {};

  ContentModel.content = Backbone.Model.extend({
    idAttribute: "mappingId",
    defaults: {
      name: "",
      description: "",
    },
    defaultValidateAttributes: ["name"],
    history: function () {
      var history = new HistoryModel.historyCollection();
      if (!this.isNew()) {
        var url = this.get("self").href + "/history";
        history.url = function () {
          return url;
        };
        history.fetch();
      }
      return history;
    },
    move: function (groupId) {
      //move the content to the group with the given ID
      //make the URL to move the content
      var kbId = Messenger.reqres.request("active:kb");
      var moveUrl = Properties.rootURL + "/" + kbId + "/mappings/groups/" + groupId + "/mappings";
      //disconnect from the collection (for some reason this doesn't always happen...)
      if (this.collection) {
        this.collection._removeReference(this);
      }
      //update the content
      this.save(null, {
        url: moveUrl,
        validate: false,
        error: function () {
          alert("error!");
        },
      });
    },
    saveModel: function (options) {
      //before saving, decide whether we should remove the model from its collection
      //(e.g. we did a modified content search and decided to ignore this content...the content should be removed from the collection)
      this._decideToRemoveModelFromCollection();
      //if new or updated, save content mapping then save urls
      if (this._allowSave(options)) {
        const wasNew = this.isNew();
        const modelOptions = this._prepareSaveModelOptions(options);
        if (!this.isNew()) {
          this.url = this.get("self").href; //set url
        }
        modelOptions.success = (model) => {
          model.dirty = false;
          //if the model was new, make a history record for it so we can navigate back to it
          if (wasNew) {
            const kbId = Messenger.reqres.request("active:kb");
            Backbone.history.navigate(kbId + "/content/" + model.get("mappingId"));
          }
          model.urls().url = model.get("urls").href;
          //save all the child models and collections of the topic
          model.saveChildren(options);
        };
        return this.save(null, modelOptions);
      } else {
        //otherwise no changes were made to the content mapping, so try to save any new/updated urls
        this.saveChildren(options);
      }
    },
    saveChildren: function (options) {
      this.saveSubModels(options);
    },
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/mappings/" + this.id;
    },
    urls: function () {
      this.urlsCol = this.getSubModel(this.urlsCol, ContentModel.urls, "/urls", false); //do not sort
      return this.urlsCol;
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return (
          this.validateRequiredValue(value, { maxLength: 100 }) ||
          this.validateSafeCharacters(value)
        );
      }
    },
    _decideToRemoveModelFromCollection: function () {
      //first, check if the model is part of a collection.  Then check if the collection is a
      //collection of modified or ignored content only
      if (this.collection) {
        //now, we need to check if the model should no longer be in the collection
        //We do this by checking each URL for whether it is modified or ignored.  If the state of any URL
        //matches the type of collection (modified/ignored), then we keep the content.  Otherwise, we need to
        //remove it from the collection
        if (this.collection.modifiedCol) {
          //check if there is a url that isModified, otherwise remove the model
          if (!this.urls().findWhere({ isModified: true })) {
            this.collection.remove(this);
          }
        } else if (this.collection.ignoredCol) {
          //check if there is an ignored url, otherwise remove the model
          if (!this.urls().findWhere({ isIgnored: true })) {
            this.collection.remove(this);
          }
        }
      }
      //otherwise, we do nothing
    },
  });

  ContentModel.url = Backbone.Model.extend({
    idAttribute: "urlId",
    defaults: {
      url: "",
      uploadId: "",
      fileName: "",
      byteSize: 0,
      isMissing: false,
      isModified: false,
      isIgnored: false,
      isUploaded: false,
    },
    defaultValidateAttributes: ["url"],
    initializeCustom: function () {
      //when the url is changed by a textbox
      this.on(
        "enter",
        function () {
          let value = this.get("url");
          let urlId = this.get("urlId");
          if (!urlId && !value) {
            // the url is not yet saved to the database. Mark this as not dirty so that server does not try to add
            // this empty url.
            this.dirty = false;
          } else {
            const defaultUrl = Messenger.reqres.request("kb:default:content:url");
            let regex;
            //If the convert to forwardslashes option is enabled, enact that now.
            const currentKb = Messenger.reqres.request("active:kb:model");
            if (currentKb.get("convertBackslashes") && !value.startsWith("\\\\")) {
              //Check whether it begins like C:\, which should not be changed.
              //This regex means that it *begins* (the ^, just like in vi) with a letter followed by a colon and a slash (escaped)
              var driveRegex = /^.:\\/;
              if (value.match(driveRegex) === null) {
                //This regex means replace all \ characters in the string.  \ is escaped.
                regex = /\\/g;
                value = value.replace(regex, "/");
                this.set("url", value);
              }
            }
            //If the default URL is the empty string, require a protocol
            if (defaultUrl.get("defaultUrl") === "") {
              //check if the value starts with http:// or https://
              if (value.length > 0 && value.indexOf("://") === -1 && !value.startsWith("\\")) {
                regex = new RegExp(".:\\.*");
                if (!regex.test(value)) {
                  value = "http://" + value;
                  this.set("url", value);
                }
              }
            } else {
              //There is a default URL.  However, still tack on http:// if the URL started with www. (indicating the user likely
              //meant to specify a specific website).
              if (value.length > 0 && value.startsWith("www.")) {
                value = "http://" + value;
                this.set("url", value);
              }
            }
          }
        },
        this,
      );
    },
    //overwrite save function to only save when the URL attribute was changed and is not empty
    _allowSave: function () {
      if (this.dirty) {
        return true;
      } else if (this.isNew()) {
        if (this.get("url")) {
          return true;
        }
      }
      return false;
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "url") {
        return this.validateStringLength(value, { maxLength: 500 });
      }
    },
  });

  ContentModel.urls = Backbone.Collection.extend({
    model: ContentModel.url,
    addNewUrl: function (touchpointId, languageId) {
      var url = new ContentModel.url({
        touchpointId: touchpointId,
        languageId: languageId,
      });
      this.add(url);
      return url;
    },
    comparator: function (a, b) {
      //sort using the touchpoint variable attached to the URL
      //if the url doesn't have a touchpoint name yet, give it a blank one
      var aTouchPoint = "";
      var bTouchPoint = "";
      if (a.touchpoint) {
        aTouchPoint = a.touchpoint;
      }
      if (b.touchpoint) {
        bTouchPoint = b.touchpoint;
      }
      if (aTouchPoint < bTouchPoint) {
        return -1;
      } else if (aTouchPoint > bTouchPoint) {
        return 1;
      } else return 0;
    },
  });

  ContentModel.batchDelete = Backbone.Model.extend({
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/mappings/batchDelete";
    },
  });

  //Request for a specific mapped content
  Messenger.reqres.setHandler("content", function (contentId) {
    var content = new ContentModel.content({ mappingId: contentId });
    content.fetch();
    return content;
  });

  //Request for the headers of the content history list
  Messenger.reqres.setHandler("content:history:headers", function () {
    var Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("history.user"),
        modelAttribute: "userName",
        sortable: true,
        filterable: true,
        textField: true,
        width: "15%",
      }),
      new Header({
        headerName: $.t("history.date"),
        modelAttribute: "dateTime",
        defaultSort: true,
        defaultReversed: true,
        sortable: true,
        dateField: true,
      }),
      new Header({
        headerName: $.t("history.change"),
        modelAttribute: "changeType",
        sortable: true,
        filterable: true,
        textField: true,
      }),
      new Header({
        headerName: $.t("history.oldValue"),
        modelAttribute: "oldValue",
        sortable: true,
        textField: true,
        width: "30%",
        customHtml: '<div class="wrap"><%- oldValue %></div>',
      }),
      new Header({
        headerName: $.t("history.newValue"),
        modelAttribute: "newValue",
        sortable: true,
        textField: true,
        width: "30%",
        customHtml: '<div class="wrap"><%- newValue %></div>',
      }),
    ]);
  });

  return ContentModel;
});
